


























































































































import * as d3 from "d3";
import Vue from "vue";
import axios from "axios";
import Expandable from "./components/Expandable.vue";

interface ClassData {
  locked: boolean,
  credits: number,
  name: string,
  displayName: string,
  label: string
}

type SectionId = "" | "university" | "classes" | "amount" | "summary" | "schedules"
type ScheduleDatum = [string, [number, number]];
type ScheduleData = ScheduleDatum[];
type NumberRange = [number, number];

const backendUrl = "https://ourscheduler.herokuapp.com";

const keepAlive: (() => void) = (() => {
  let timeoutId: number;

  return function ping(): void {
    clearTimeout(timeoutId);
    axios.get(`${ backendUrl }/keep-alive`);
    timeoutId = setTimeout(ping, 3 * 60 * 1000);
  };
})();

export default Vue.extend({
  name: "App",
  data() {
    return {
      loading: false as boolean,
      projectName: "OurScheduler" as string,
      expandedSection: "" as SectionId,
      schoolNameTemp: "" as string,
      classTemp: "" as string,
      classesSet: [] as ClassData[],
      classAmountTemp: "" as string,
      creditAmountTemp: "" as string,
      classAmountSet: null as (NumberRange | null),
      creditAmountSet: null as (NumberRange | null),
      schoolId: "" as string,
      generatedSchedules: null as (ScheduleData[] | null),
      currentScheduleIndex: null as (number | null),
      noShuffle: false as boolean,
      prevProp: "" as string
    };
  },
  watch: {
    expandedSection(toBeExpanded: string): void {
      switch (toBeExpanded) {
        case "university":
          this.schoolNameTemp = this.schoolId;
          break;
        case "classes":
          this.classTemp = "";
          break;
        case "amount":
          if (this.classAmountSet && this.creditAmountSet) {
            this.classAmountTemp = this.classAmountFormatted;
            this.creditAmountTemp = this.creditAmountFormatted;
          } else if (this.classAmountSet) {
            this.classAmountTemp = this.classAmountFormatted;
            this.creditAmountTemp = "";
          } else if (this.creditAmountSet) {
            this.creditAmountTemp = this.creditAmountFormatted;
            this.classAmountTemp = "";
          } else {
            this.classAmountTemp = "";
            this.creditAmountTemp = "";
          }
          break;
      }
    },
    classesSet(): void {
      this.currentScheduleIndex = null;
    }
  },
  mounted(): void {
    setTimeout(() => {
      this.expand("university");
    }, 100);
    keepAlive();
  },
  computed: {
    allowAddClass(): boolean {
      return !!this.classTemp && !this.classesSet.find((e) => e.name === this.classTemp.trim().toUpperCase() || e.label === this.classTemp.trim().toUpperCase());
    },
    isClassAmountValid(): boolean {
      return /^(?:1[0-9]|[1-9])(?:-(?:1[0-9]|[1-9]))?$/.test(this.classAmountTemp);
    },
    isCreditAmountValid(): boolean {
      return /^(?:[1-3][0-9]|[1-9])(?:-(?:[1-3][0-9]|[1-9]))?$/.test(this.creditAmountTemp);
    },
    isClassCreditValid(): boolean {
      return (!!this.classAmountTemp || !!this.creditAmountTemp) && (!this.classAmountTemp || this.isClassAmountValid) && (!this.creditAmountTemp || this.isCreditAmountValid);
    },
    classAmountFormatted(): string {
      if (this.classAmountSet) {
        return this.classAmountSet[0] === this.classAmountSet[1] ? `${ this.classAmountSet[0] }` : this.classAmountSet.join("-");
      }
      return "";
    },
    creditAmountFormatted(): string {
      if (this.creditAmountSet) {
        return this.creditAmountSet[0] === this.creditAmountSet[1] ? `${ this.creditAmountSet[0] }` : this.creditAmountSet.join("-");
      }
      return "";
    },
    creditSum(): number {
      return d3.sum(this.classesSet, (el: ClassData) => el.credits);
    },
    lockedCreditSum(): number {
      return d3.sum(this.classesSet.filter((el: ClassData) => el.locked), (el: ClassData) => el.credits);
    },
    currentScheduleClasses(): Set<string> {
      if (this.generatedSchedules === null || this.currentScheduleIndex === null) { return new Set<string>(); }
      return new Set<string>(this.generatedSchedules[this.currentScheduleIndex].map((el: ScheduleDatum) => el[0]));
    },
    currentScheduleCreditSum(): number {
      return d3.sum(this.classesSet.filter((el: ClassData) => this.currentScheduleClasses.has(el.name)), (el: ClassData) => el.credits);
    },
    currentScheduleClassAmountFormatted(): string {
      const classAmount = this.currentScheduleClasses.size;
      return classAmount + " Class" + (classAmount === 1 ? "" : "es");
    },
    currentScheduleCreditAmountFormatted(): string {
      return this.currentScheduleCreditSum + " Credit" + (this.currentScheduleCreditSum === 1 ? "" : "es");
    }
  },
  components: {
    Expandable
  },
  methods: {
    expand(id: SectionId, focus: boolean = false): void {
      this.$nextTick(() => {
        this.expandedSection = id;

        if (focus) {
          const inputEl: HTMLElement = this.$refs[id + "-input"];
          this.moveFocusTo(inputEl, 505);
        }
      });
    },
    collapse(focus: boolean = false): void {
      this.$nextTick(() => {
        if (focus && !!this.expandedSection) {
          const inputEl: HTMLElement = this.$refs[this.expandedSection + "-toggle"];
          this.moveFocusTo(inputEl, 505);
        }

        this.expandedSection = "";
      });
    },
    toggle(id: SectionId, focus: boolean = false): void {
      this.$nextTick(() => {
        this.expandedSection = (this.expandedSection === id) ? "" : id;

        if (focus) {
          const inputEl: HTMLElement = this.$refs[id + (this.expandedSection ? "-input" : "-toggle")];
          this.moveFocusTo(inputEl, 505);
        }
      });
    },
    moveFocusTo: (() => {
      let timeoutId: number;

      return (element: HTMLElement, delay: number) => {
        clearTimeout(timeoutId);
        const focusedEl = document.activeElement;

        if (!!focusedEl && !!element) {
          timeoutId = setTimeout(() => {
            if (document.activeElement === focusedEl) {
              element.focus();
            }
          }, delay);
        }
      };
    })(),
    moveStarRemoveFocus(index: number, toLeft: boolean): void {
      const el: HTMLButtonElement[] = this.$refs[toLeft ? "star" : "remove"];

      if (!!el && !!el[index]) {
        el[index].focus();
      }
    },
    handleShiftTab(index: number, event: KeyboardEvent): void {
      if (!event.shiftKey) {
        return;
      }

      event.preventDefault();
      if (index === 0) {
        if (!this.allowAddClass || this.loading) {
          const inputEl: HTMLElement = this.$refs["classes-input"];
          inputEl.focus();
        } else {
          const submitEl: HTMLElement = this.$refs["classes-submit"];
          submitEl.focus();
        }
      } else {
        this.moveStarRemoveFocus(index - 1, true);
      }
    },
    removeClass(index: number): void {
      const classData = this.classesSet[index];

      if (classData && confirm(`${ classData.displayName } will be removed from your schedule.`)) {
        this.classesSet.splice(index, 1);
      }
    },
    setSchoolUrl(focus: boolean = false): void {
      if (this.loading) {return;}

      if (this.schoolId !== this.schoolNameTemp) {
        this.classTemp = "";
        this.classesSet = [];
        this.currentScheduleIndex = null;
      } else {
        this.expand("classes", focus);
        return;
      }

      this.loading = true;

      try {
        this.schoolId = "";

        const target = this.schoolNameTemp;

        axios.get(`${ backendUrl }/search/${ target }`).then(({ data }) => {
          if (!!data && typeof data === "string") {
            this.schoolId = data.toUpperCase();
            this.expand("classes", focus);
          } else {
            alert("Unfortunately, something went wrong.");
          }
          this.loading = false;
        }, (e) => {
          console.error(e);
          alert("Unfortunately, something went wrong.");
          this.loading = false;
        }).catch((e) => {
          console.error(e);
          alert("Unfortunately, something went wrong.");
          this.loading = false;
        });
      } catch (e) {
        console.error(e);
        alert("Unfortunately, something went wrong.");
        this.loading = false;
      }
    },
    addClass(): void {
      if (this.loading) {return;}

      this.currentScheduleIndex = null;
      this.loading = true;

      const school = this.schoolId.toLowerCase();
      const target = this.classTemp.trim().toUpperCase();
      try {
        axios.get(`${ backendUrl }/retrieve/${ school }/${ target }`).then(({ data }) => {
          if (data) {
            this.classTemp = "";

            const displayName = `${ data.name } (${ data.label })`;
            if (!this.classesSet.find((el) => el.name === data.name)) {
              this.classesSet.push({
                locked: false,
                credits: data.credits,
                name: data.name,
                displayName: displayName,
                label: data.label
              });
            }
            this.loading = false;
          } else {
            alert("Unfortunately, this class does not exist.");
            this.loading = false;
          }
        }, (e) => {
          console.error(e);
          alert("Unfortunately, something went wrong.");
          this.loading = false;
        }).catch((e) => {
          console.error(e);
          alert("Unfortunately, something went wrong.");
          this.loading = false;
        });
      } catch (e) {
        console.error(e);
        alert("Unfortunately, something went wrong.");
        this.loading = false;
      }
    },
    toggleClassLock(index: number): void {
      this.classesSet[index].locked = !this.classesSet[index].locked;
      this.currentScheduleIndex = null;
    },
    setClassCredit(focus: boolean = false): void {
      if (this.classAmountTemp !== this.classAmountFormatted || this.creditAmountTemp !== this.creditAmountFormatted) {
        this.currentScheduleIndex = null;
      }

      if (this.classAmountTemp) {
        const range = this.classAmountTemp.split("-").map(Number);
        this.classAmountSet = range.length === 1 ? [range[0], range[0]] : [Math.min(...range), Math.max(...range)];
      } else {
        this.classAmountSet = null;
      }

      if (this.creditAmountTemp) {
        const range = this.creditAmountTemp.split("-").map(Number);
        this.creditAmountSet = range.length === 1 ? [range[0], range[0]] : [Math.min(...range), Math.max(...range)];
      } else {
        this.creditAmountSet = null;
      }

      this.expand(this.classesSet.length ? "summary" : "classes", focus);
    },
    classAmountFormattedLong(cap: boolean): string {
      if (this.classAmountSet) {
        return this.classAmountFormatted + (cap ? " Class" : " class") + (this.classAmountFormatted === "1" ? "" : "es");
      }
      return "";
    },
    creditAmountFormattedLong(cap: boolean): string {
      if (this.creditAmountSet) {
        return this.creditAmountFormatted + (cap ? " Credit" : " credit") + (this.creditAmountFormatted === "1" ? "" : "s");
      }
      return "";
    },
    generateSchedule(focus: boolean = false): void {
      if (this.loading) {return;}

      let notEnoughClassSelected = false;
      let notEnoughCreditSelected = false;
      let tooManyClassLocked = false;
      let tooManyCreditLocked = false;

      if (this.classAmountSet) {
        notEnoughClassSelected = this.classAmountSet[0] > this.classesSet.length;
        tooManyClassLocked = this.classAmountSet[1] < this.classesSet.filter(el => el.locked).length;
      }

      if (this.creditAmountSet) {
        notEnoughCreditSelected = this.creditAmountSet[0] > this.creditSum;
        tooManyCreditLocked = this.creditAmountSet[1] < this.lockedCreditSum;
      }

      const notEnoughSelected = notEnoughClassSelected || notEnoughCreditSelected;
      const tooMuchLocked = tooManyClassLocked || tooManyCreditLocked;
      const classIssue = notEnoughClassSelected || tooManyClassLocked;
      const creditIssue = notEnoughCreditSelected || tooManyCreditLocked;

      if (notEnoughSelected || tooMuchLocked) {
        alert(`Please ${ notEnoughSelected ? "add more classes" : "" }${ notEnoughSelected && tooMuchLocked ? " and " : "" }${ tooMuchLocked ? "unlock some classes" : "" } so that we can meet your ${ classIssue ? this.classAmountFormattedLong(false) : "" }${ classIssue && creditIssue ? " and " : "" }${ creditIssue ? this.creditAmountFormattedLong(false) : "" } requirement.`);
        return;
      }

      const prop = {
        classList: this.classesSet.map((el) => ([this.schoolId.toLowerCase(), el.label.toUpperCase()])),
        locked: this.classesSet.filter((el) => el.locked).map((el) => el.label),
        restrictions: {
          minCredit: this.creditAmountSet && this.creditAmountSet[0] || 1,
          maxCredit: this.creditAmountSet && this.creditAmountSet[1] || 39,
          minCourses: this.classAmountSet && this.classAmountSet[0] || 1,
          maxCourses: this.classAmountSet && this.classAmountSet[1] || 19
        }
      };

      const propStr = JSON.stringify(prop);

      if (this.prevProp === propStr && this.generatedSchedules) {
        this.currentScheduleIndex = 0;
        this.expand("schedules", focus);
        return;
      } else {
        this.loading = true;
        this.prevProp = propStr;
      }

      this.currentScheduleIndex = null;
      this.generatedSchedules = null;

      try {
        axios.get(`${ backendUrl }/generate/?prop=${ encodeURI(JSON.stringify(prop)) }`).then(({ data }) => {
          this.runD3(data[0]);
          this.currentScheduleIndex = 0;
          this.noShuffle = data.length < 2;
          this.generatedSchedules = data;
          this.loading = false;
          this.expand("schedules", focus);
        }, (e) => {
          console.error(e);
          alert("Unfortunately, something went wrong.");
          this.loading = false;
        }).catch((e) => {
          console.error(e);
          alert("Unfortunately, something went wrong.");
          this.loading = false;
        });
      } catch (e) {
        console.error(e);
        alert("Unfortunately, something went wrong.");
        this.loading = false;
      }
    },
    runD3(schedules: ScheduleData): void {
      interface CalendarEvent {
        timeFrom: number,
        timeTo: number,
        title: string,
        day: number
      }

      const START_TIME: number = Date.parse("1 Jan 1970 00:00:00").valueOf();
      const END_TIME: number = START_TIME + 24 * 60 * 60 * 1000;
      const calendarEvents: CalendarEvent[] = [];
      for (let i = 0; i < schedules.length; i++) {
        calendarEvents.push({
          timeFrom: schedules[i][1][0] % 1440 * 60000 + START_TIME,
          timeTo: schedules[i][1][1] % 1440 * 60000 + START_TIME,
          title: schedules[i][0],
          day: Math.floor(schedules[i][1][0] / 1440)
        });
      }

      const minDateValue: number = d3.min(calendarEvents.map((d: CalendarEvent) => d.timeFrom)) ?? START_TIME;
      const maxDateValue: number = d3.max(calendarEvents.map((d: CalendarEvent) => d.timeTo)) ?? END_TIME;
      const margin = { top: 30, right: 10, bottom: 5, left: 40 };
      const hours: number = (maxDateValue - minDateValue) / 60 / 60000 + 2 / 3;
      const width: number = 920;
      const barHeight: number = 1350 * (hours / (24 + 2 / 3));
      const barWidth: number = width - margin.left - margin.right;
      const height: number = barHeight + margin.top + margin.bottom;

      // Create the SVG element
      const svg = d3.create("svg").attr("width", width).attr("height", height);
      // All further code additions will go just below this line
      const yScale = d3.scaleTime().domain([new Date(minDateValue - 1 / 3 * 60 * 60 * 1000), new Date(maxDateValue + 1 / 3 * 60 * 60 * 1000)]).range([margin.top, height - margin.bottom]);
      const yAxis = d3.axisLeft<Date>(yScale).ticks(hours).tickFormat((d: Date) => d.toLocaleTimeString("en-US", {
        hour: "numeric",
        hour12: true
      }));
      const xAxis = d3.axisTop(d3.scaleLinear().domain([0, 6]).range([margin.left, width - margin.right])).ticks(6).tickSize(height - margin.bottom - margin.top).tickFormat(() => "");

      svg.append("g").attr("transform", `translate(${ margin.left },0)`).call(yAxis).call(g => g.select(".domain").remove());

      svg.append("g").attr("transform", `translate(0,${ height - margin.bottom })`).attr("opacity", 0.3).call(xAxis).call(g => g.select(".domain").remove()).call(g => g.selectAll(".tick").filter(d => d === 0).remove());

      const gridLines = d3.axisRight(yScale).ticks(hours * 4).tickSize(barWidth).tickFormat(() => ""); // even though they're "ticks" we've set them to be full-width
      svg.append("text").attr("font-family", "'Open Sans', sans-serif").attr("font-size", 20).attr("font-weight", 500).attr("text-anchor", "middle").attr("x", barWidth / 12 + margin.left).attr("y", 20).text("Mon");
      svg.append("text").attr("font-family", "'Open Sans', sans-serif").attr("font-size", 20).attr("font-weight", 500).attr("text-anchor", "middle").attr("x", 3 * barWidth / 12 + margin.left).attr("y", 20).text("Tue");
      svg.append("text").attr("font-family", "'Open Sans', sans-serif").attr("font-size", 20).attr("font-weight", 500).attr("text-anchor", "middle").attr("x", 5 * barWidth / 12 + margin.left).attr("y", 20).text("Wed");
      svg.append("text").attr("font-family", "'Open Sans', sans-serif").attr("font-size", 20).attr("font-weight", 500).attr("text-anchor", "middle").attr("x", 7 * barWidth / 12 + margin.left).attr("y", 20).text("Thu");
      svg.append("text").attr("font-family", "'Open Sans', sans-serif").attr("font-size", 20).attr("font-weight", 500).attr("text-anchor", "middle").attr("x", 9 * barWidth / 12 + margin.left).attr("y", 20).text("Fri");
      svg.append("text").attr("font-family", "'Open Sans', sans-serif").attr("font-size", 20).attr("font-weight", 500).attr("text-anchor", "middle").attr("x", 11 * barWidth / 12 + margin.left).attr("y", 20).text("Sat");
      svg.append("g").attr("transform", `translate(${ margin.left },0)`).call(gridLines).call(g => g.select(".domain").attr("opacity", 0.3)).call(g => g.selectAll(".tick").attr("opacity", (d) => (d as Date).getMinutes() === 0 ? 0.3 : 0.1));
      const barGroups = svg.selectAll("g.barGroup").data(calendarEvents).join("g").attr("class", "barGroup");
      barGroups.append("rect").attr("stroke-width", "2px").attr("x", d => margin.left + barWidth / 6 * d.day + 1).attr("y", d => yScale(new Date(d.timeFrom)) + 1).attr("height", d => yScale(new Date(d.timeTo)) - yScale(new Date(d.timeFrom)) - 2).attr("width", barWidth / 6 - 2);
      // barGroups.append("text").attr("font-family", "'Open Sans', sans-serif").attr("font-size", 8).attr("font-weight", 500).attr("text-anchor", "middle").attr("fill", barStyle.textColor).attr("x", d => (1 + 2 * d.day) * barWidth / 12 + margin.left).attr("y", d => yScale(new Date(d.timeFrom)) + 20).text(d => d.title);

      barGroups.append("foreignObject").attr("x", d => margin.left + barWidth / 6 * d.day + 2).attr("y", d => yScale(new Date(d.timeFrom)) + 2).attr("width", barWidth / 6 - 4).attr("height", d => yScale(new Date(d.timeTo)) - yScale(new Date(d.timeFrom)) - 4).append("xhtml:body").style("font-weight", "500").html(d => `<div class="block__text"><p>${ d.title }</p><p>${ new Date(d.timeFrom).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true
      }) }–${ new Date(d.timeTo).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true
      }) }</p></div>`);

      // Actually add the element to the page
      const element = this.$refs["d3-schedule"];
      while (element.firstChild) {
        element.removeChild(element.firstChild);
      }
      element.append(svg.node());
      // This part ^ always goes at the end of our index.js
    },
    swapSchedule(): void {
      if (this.noShuffle || !this.generatedSchedules || this.currentScheduleIndex === null) { return; }

      let currStr: string = "";
      let nextStr: string = JSON.stringify(this.generatedSchedules[this.currentScheduleIndex]);
      const lastIndex: number = this.currentScheduleIndex;

      do {
        currStr = nextStr;
        this.currentScheduleIndex = (this.currentScheduleIndex + 1) % this.generatedSchedules.length;
        nextStr = JSON.stringify(this.generatedSchedules[this.currentScheduleIndex]);
      } while (nextStr === currStr && lastIndex !== this.currentScheduleIndex);

      if (lastIndex === this.currentScheduleIndex) {
        this.noShuffle = true;
      } else {
        this.runD3(this.generatedSchedules[this.currentScheduleIndex]);
      }
    }
  }
});
